import React, { useState, useEffect, useCallback } from 'react';
import Modal from '@material-ui/core/Modal';
import Backdrop from '@material-ui/core/Backdrop';
import Fade from '@material-ui/core/Fade';
import { Card, CardBody, CardHeader, Col, Row } from 'reactstrap';
import MaterialCheckbox from '../../../../shared/components/material-checkbox/MaterialCheckbox';
import DataTableProgressComponent from '../../../../shared/components/widget/DataTableProgressComponent';
import DataTableNoDataComponent from '../../../../shared/components/widget/DataTableNoDataComponent';
import DataTable from 'react-data-table-component';
import memoize from 'memoize-one';
import { getCashbacks } from '../../../../actions/cashbackActions';
import { useDispatch } from 'react-redux';
import '../../../brand/Modal.css';

const columns = memoize((handleEditClick, handleShowClick, handleDeleteClick) => [
  {
    name: 'ID',
    selector: 'id',
    sortable: true,
    center: true,
    grow: 0
  },
  {
    name: 'Brand',
    sortable: false,
    grow: 1,
    cell: row => <div>{row.brand != null ? row.brand.name : ''}</div>
  },
  {
    name: 'Title',
    selector: 'title',
    sortable: true,
    grow: 1.5
  },
  {
    name: 'Start date',
    sortable: true,
    cell: row => <div>{row.start_date != null ? row.start_date.substring(0, 10) : ''}</div>,
    grow: 1
  },
  {
    name: 'End date',
    sortable: true,
    grow: 1,
    cell: row => <div>{row.end_date != null ? row.end_date.substring(0, 10) : ''}</div>
  },
  {
    name: 'Amount',
    selector: "amount",
    sortable: true,
    center: true,
    grow: 1,
    cell: row => <div>{row.amount + '' + row.unit}</div>
  },
  {
    name: 'Unit',
    selector: 'unit',
    sortable: true,
    center: true,
    grow: 1
  },
  {
    name: 'Status',
    selector: 'active',
    sortable: false,
    cell: row => <span>{row.active === true ? 'Active' : 'Inactive'}</span>,
    right: false,
    center: true,
    grow: 1
  },
  {
    name: 'Actions',
    sortable: false,
    left: true,
    cell: (row) => <div>
      <button className="btn btn-sm btn-light mr-2" onClick={handleEditClick} id={row.id}>
        <i className="icon-pencil" onClick={handleEditClick} id={row.id}></i>
      </button>
      <button className="btn btn-sm btn-light mr-2" onClick={handleDeleteClick} id={row.id}>
        <i className="icon-trash" id={row.id}></i>
      </button>
      <button className="btn btn-sm btn-light mr-2" onClick={handleShowClick} id={row.id}>
        <i className="icon-chart" onClick={handleShowClick} id={row.id}></i>
      </button>
    </div>,
    ignoreRowClick: true
  }
]);

const selectProps = { indeterminate: isIndeterminate => isIndeterminate };

const BrandModal = ({ open, onClose, brandId }) => {
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [totalRows, setTotalRows] = useState(0);
  const [perPage, setPerPage] = useState(10);
  const [sort, setSort] = useState(false);
  const [search, setSearch] = useState(null);
  const [error, setError] = useState(null);
  const dispatch = useDispatch();

  // Load cashbacks filtered by brand ID
  const loadCashbacks = useCallback(async (params = {}) => {
    setLoading(true);
    try {
      const searchParams = {
        per_page: perPage,
        ...params,
        ...(sort && sort),
        ...(search && search)
      };

      // Add brand ID filter if provided
      if (brandId) {
        searchParams['search[brand]'] = brandId;
      }

      const result = await dispatch(getCashbacks(searchParams));
      setData(result.payload.items);
      setTotalRows(result.payload.meta.total_items);
    } catch (error) {
      setError(error);
      console.error('Error loading cashbacks:', error);
    } finally {
      setLoading(false);
    }
  }, [dispatch, perPage, sort, search, brandId]);

  useEffect(() => {
    if (open && brandId) {
      loadCashbacks();
    }
  }, [open, brandId, loadCashbacks]);

  const handlePageChange = async (page) => {
    await loadCashbacks({ page });
  };

  const handlePerRowsChange = async (perPageValue, page) => {
    setPerPage(perPageValue);
    await loadCashbacks({ per_page: perPageValue, page });
  };

  const handleSort = async (column, sortDirection) => {
    const sortObj = {};
    sortObj['order[' + column.selector + ']'] = sortDirection;
    setSort(sortObj);
    await loadCashbacks(sortObj);
  };

  const handleEditClick = (event) => {
    // Navigate to edit page
    window.open(`/cashbacks/edit/${event.target.id}`, '_blank');
  };

  const handleShowClick = (event) => {
    // Navigate to show page
    window.open(`/cashbacks/show/${event.target.id}`, '_blank');
  };

  const handleDeleteClick = (event) => {
    console.log('Delete cashback:', event.target.id);
     window.open(`/cashbacks/show/${event.target.id}`, '_blank');
    // Implement delete functionality if needed
  };

  const handleClose = () => {
    setData([]);
    setError(null);
    setSort(false);
    setSearch(null);
    onClose();
  };

  const handleChange = (state) => {
    console.log('Selected Rows: ', state.selectedRows);
  };

  return (
    <Modal
      disablePortal
      disableEnforceFocus
      disableAutoFocus
      open={open}
      onClose={handleClose}
      closeAfterTransition
      BackdropComponent={Backdrop}
      BackdropProps={{
        timeout: 500,
      }}
    >
      <Fade in={open}>
        <div className="material-modal-content" style={{ width: '90%', maxWidth: '1200px' }}>
          <h4 id="brand-modal-title" className="mb-4">
            Brand Cashbacks {brandId && `- Brand ID: ${brandId}`}
            <i className="cil-x cursor-pointer float-right" onClick={handleClose}></i>
          </h4>
          <div id="brand-modal-description">
            {error ? (
              <div className="text-danger">Error: {error.toString()}</div>
            ) : (
              <Row>
                <Col xs="12" lg="12">
                  <Card>
                    <CardHeader>
                      <div className="d-flex align-items-center justify-content-between">
                        <div><i className="fa fa-align-justify"></i>Cashbacks list</div>
                      </div>
                    </CardHeader>
                    <CardBody>
                      <DataTable
                        noHeader={false}
                        columns={columns(handleEditClick, handleShowClick, handleDeleteClick)}
                        data={data}
                        theme="solarized"
                        progressPending={loading}
                        pagination
                        paginationServer
                        paginationTotalRows={totalRows}
                        paginationRowsPerPageOptions={[10, 50, 100]}
                        onChangeRowsPerPage={handlePerRowsChange}
                        onChangePage={handlePageChange}
                        onSort={handleSort}
                        sortServer
                        selectableRows
                        selectableRowsComponent={MaterialCheckbox}
                        selectableRowsComponentProps={selectProps}
                        onSelectedRowsChange={handleChange}
                        progressComponent={<DataTableProgressComponent />}
                        noDataComponent={<DataTableNoDataComponent />}
                      />
                    </CardBody>
                  </Card>
                </Col>
              </Row>
            )}
          </div>
        </div>
      </Fade>
    </Modal>
  );
};

export default BrandModal;
